import { collection, getDocs, doc, getDoc, updateDoc, addDoc, deleteDoc, query, orderBy, where } from 'firebase/firestore';
import { db, auth } from '../../../../firebase/config';
import { fetchPSOEmail } from '../../../../api/emailService';
import { setDoc } from 'firebase/firestore';

export type Request = {
    id: string;
    requestId?: string;
    requestType?: string;
    createdBy?: string;
    branchCode?: string;
    branchName?: string;
    company?: string;
    companyId?: string;
    requesterName?: string;
    requesterContact?: string;
    requiredBy?: string;
    cctvDateFrom?: any;
    cctvDateTo?: any;
    guardDateFrom?: any;
    guardDateTo?: any;
    bamsDateFrom?: any;
    bamsDateTo?: any;
    timeFrom?: string;
    timeTo?: string;
    numberOfGuards?: number;
    hardwareName?: string;
    purpose?: string;
    purposeOther?: string;
    status?: string;
    createdAt?: any;
    updatedAt?: any;
    forwardedAt?: any;
    forwardedBy?: string;
    updatedBy?: string;
    guardCompany?: string;
    branchId?: string;
    guardDailyRate?: number;
    totalAmount?: number;
    [key: string]: any;
};

export type CompanyInfo = {
    companyId: string;
    psoId: string;
    psoDesignation: string;
    psoName: string;
    psoEmail: string;
};

export type Branch = {
    id: string;
    companyId: string;
    branchCode?: string;
    branchName?: string;
    psoId?: string;
    [key: string]: any;
};

/**
 * Fetch company ID and PSO information for the current user
 */
export async function fetchCompanyIdAndPsoInfo(uid: string): Promise<CompanyInfo | null> {
    try {
        const banksSnap = await getDocs(collection(db, 'banks'));
        for (const bankDoc of banksSnap.docs) {
            const posDocRef = doc(db, 'banks', bankDoc.id, 'pos', uid);
            const posDoc = await getDoc(posDocRef);
            if (posDoc.exists()) {
                const posData = posDoc.data();
                const psoEmailFromDB = await fetchPSOEmail(db, bankDoc.id, uid);

                return {
                    companyId: bankDoc.id,
                    psoId: posData.psoId || uid,
                    psoDesignation: posData.designation || 'PSO',
                    psoName: posData.name || '',
                    psoEmail: psoEmailFromDB || '',
                };
            }
        }
        return null;
    } catch (error) {
        console.error('Error fetching company ID and PSO info:', error);
        return null;
    }
}

/**
 * Fetch branches for a company, filtered by PSO ID
 */
export async function fetchBranches(companyId: string, psoId: string): Promise<Branch[]> {
    try {
        const branchSnap = await getDocs(collection(db, 'banks', companyId, 'branch'));
        const branchList = branchSnap.docs.map(doc => ({
            id: doc.id,
            companyId,
            ...doc.data(),
        }));

        // Filter branches by PSO ID
        const filteredBranches = branchList.filter((branch: any) => {
            return branch.psoId === psoId;
        });

        return filteredBranches as Branch[];
    } catch (error) {
        console.error('Error fetching branches:', error);
        throw error;
    }
}

/**
 * Fetch managed PSOs for a Regional PSO or Head PSO
 */
export async function fetchManagedPsos(
    companyId: string,
    psoId: string,
    psoDesignation: string,
    uid: string
): Promise<string[]> {
    if (psoDesignation !== 'Regional PSO' && psoDesignation !== 'Head PSO') {
        return [];
    }

    try {
        let allManagedIds: string[] = [];

        // Fetch directly managed PSOs
        const managedSnap = await getDocs(collection(db, 'banks', companyId, 'pos', uid, 'managedPsos'));
        const directManagedIds = managedSnap.docs.map(doc => doc.data().psoId);
        allManagedIds = [...directManagedIds];

        // If this is a Head PSO, also fetch PSOs managed by Regional PSOs
        if (psoDesignation === 'Head PSO') {
            for (const regionalPsoId of directManagedIds) {
                try {
                    const regionalPsoDoc = await getDoc(doc(db, 'banks', companyId, 'pos', regionalPsoId));
                    if (regionalPsoDoc.exists()) {
                        const regionalPsoAuthUid = regionalPsoDoc.id;
                        const regionalManagedSnap = await getDocs(
                            collection(db, 'banks', companyId, 'pos', regionalPsoAuthUid, 'managedPsos')
                        );
                        const regionalManagedIds = regionalManagedSnap.docs.map(doc => doc.data().psoId);
                        allManagedIds = [...allManagedIds, ...regionalManagedIds];
                    }
                } catch (error) {
                    console.error('Error fetching Regional PSO managed PSOs:', error);
                }
            }
        }

        return allManagedIds;
    } catch (error) {
        console.error('Error fetching managed PSOs:', error);
        return [];
    }
}

/**
 * Fetch all requests for a company, filtered by PSO designation
 */
export async function fetchRequests(
    companyId: string,
    psoId: string,
    psoDesignation: string | null,
    managedPsoIds: string[]
): Promise<Request[]> {
    try {
        const requestsSnap = await getDocs(
            query(
                collection(db, 'banks', companyId, 'requests'),
                orderBy('createdAt', 'desc')
            )
        );
        const allRequests = requestsSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as Request[];

        // Filter to show requests based on PSO designation
        const requestsList = allRequests.filter(request => {
            let matches = false;

            // Check if user is an incharge
            const isIncharge = psoDesignation && /incharge/i.test(psoDesignation);

            if (psoDesignation === 'PSO') {
                matches = request.createdBy === psoId;
            } else if (psoDesignation === 'Regional PSO') {
                // Regional Head: Show all requests from managed PSOs (not their own requests)
                const isManagedRequest = managedPsoIds.includes(request.createdBy || '');
                matches = isManagedRequest;
            } else if (psoDesignation === 'Head PSO') {
                matches = request.status === 'forwarded_to_head';
            } else if (isIncharge) {
                // Incharge users can see all requests (filtering by type will be done in UI)
                matches = true;
            } else {
                // Default: Bank users or users without a specific PSO role see all requests
                matches = true;
            }

            return matches;
        });

        // Sort requests: pending first, then by creation date (newest first)
        requestsList.sort((a, b) => {
            const aIsPending = a.status === 'pending';
            const bIsPending = b.status === 'pending';

            // If one is pending and the other isn't, pending comes first
            if (aIsPending && !bIsPending) return -1;
            if (!aIsPending && bIsPending) return 1;

            // If both have same pending status, sort by creation date (newest first)
            const aDate = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt?.seconds || 0) * 1000;
            const bDate = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt?.seconds || 0) * 1000;
            return bDate - aDate;
        });

        return requestsList;
    } catch (error) {
        console.error('Error fetching requests:', error);
        throw error;
    }
}

/**
 * Create a new request
 */
export async function createRequest(
    companyId: string,
    requestData: Partial<Request>
): Promise<string> {
    try {
        let finalRequestData = {
            ...requestData,
            createdAt: new Date(),
        };

        // If it's a Guard request, fetch the current rate and calculate total
        if (requestData.requestType === 'Guard') {
            const currentRate = await fetchGuardRate();
            const numberOfGuards = Number(requestData.numberOfGuards) || 0;

            // Calculate days
            let days = 0;
            if (requestData.guardDateFrom && requestData.guardDateTo) {
                const dateFrom = new Date(requestData.guardDateFrom);
                const dateTo = new Date(requestData.guardDateTo);
                const diffTime = Math.abs(dateTo.getTime() - dateFrom.getTime());
                days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // Inclusive of start and end date
            } else if (requestData.cctvDateFrom && requestData.cctvDateTo) {
                // Fallback to cctvDate fields if guardDate fields are not set (as shared in form)
                const dateFrom = new Date(requestData.cctvDateFrom);
                const dateTo = new Date(requestData.cctvDateTo);
                const diffTime = Math.abs(dateTo.getTime() - dateFrom.getTime());
                days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
            }

            const totalAmount = numberOfGuards * currentRate * days;

            finalRequestData = {
                ...finalRequestData,
                guardDailyRate: currentRate,
                totalAmount: totalAmount
            };
        }

        const docRef = await addDoc(collection(db, 'banks', companyId, 'requests'), finalRequestData);
        return docRef.id;
    } catch (error) {
        console.error('Error creating request:', error);
        throw error;
    }
}

/**
 * Update request status
 */
export async function updateRequestStatus(
    companyId: string,
    requestId: string,
    status: string,
    uid: string
): Promise<void> {
    try {
        await updateDoc(doc(db, 'banks', companyId, 'requests', requestId), {
            status,
            updatedAt: new Date(),
            updatedBy: uid,
        });
    } catch (error) {
        console.error('Error updating request status:', error);
        throw error;
    }
}

/**
 * Permanently remove a request document from Firestore.
 */
export async function deleteRequest(companyId: string, requestId: string): Promise<void> {
    try {
        await deleteDoc(doc(db, 'banks', companyId, 'requests', requestId));
    } catch (error) {
        console.error('Error deleting request:', error);
        throw error;
    }
}

/**
 * Forward request to Head PSO
 */
export async function forwardRequestToHead(
    companyId: string,
    requestId: string,
    uid: string
): Promise<void> {
    try {
        await updateDoc(doc(db, 'banks', companyId, 'requests', requestId), {
            status: 'forwarded_to_head',
            forwardedAt: new Date(),
            forwardedBy: uid,
            updatedAt: new Date(),
            updatedBy: uid,
        });
    } catch (error) {
        console.error('Error forwarding request to head:', error);
        throw error;
    }
}

export type Company = {
    id: string;
    companyId?: string;
    companyName?: string;
    companyType?: string;
    [key: string]: any;
};

/**
 * Fetch available companies based on request type
 */
export async function fetchAvailableCompanies(
    companyId: string,
    requestType: string
): Promise<Company[]> {
    try {
        const companiesSnap = await getDocs(collection(db, 'banks', companyId, 'companies'));
        const companiesList: Company[] = [];

        let targetCompanyType = '';
        if (requestType === 'CCTV') {
            targetCompanyType = 'CCTV';
        } else if (requestType === 'Guard') {
            targetCompanyType = 'Guard Company';
        } else if (requestType === 'BAMS') {
            targetCompanyType = 'BAMS';
        }

        for (const companyDoc of companiesSnap.docs) {
            const companyData = companyDoc.data();

            if (companyData.companyType === targetCompanyType) {
                if (companyData.companyId && !companyData.companyName) {
                    const globalCompanyDoc = await getDoc(doc(db, 'companies', companyData.companyId));
                    if (globalCompanyDoc.exists()) {
                        const globalCompanyData = globalCompanyDoc.data();
                        companiesList.push({
                            id: companyDoc.id,
                            companyId: globalCompanyData.companyId,
                            companyName: globalCompanyData.companyName,
                            companyType: companyData.companyType,
                            companyAddress: globalCompanyData.companyAddress,
                            companyNo: globalCompanyData.companyNo,
                            email: globalCompanyData.email,
                            isGlobal: true,
                            ...companyData,
                        });
                    }
                } else {
                    companiesList.push({
                        id: companyDoc.id,
                        companyId: companyData.companyId || companyDoc.id,
                        companyName: companyData.companyName,
                        companyType: companyData.companyType,
                        companyAddress: companyData.companyAddress,
                        companyNo: companyData.companyNo,
                        email: companyData.email,
                        isGlobal: false,
                        ...companyData,
                    } as Company);
                }
            }
        }

        return companiesList;
    } catch (error) {
        console.error('Error fetching available companies:', error);
        throw error;
    }
}

/**
 * Fetch the current daily income rate for guards
 */
export async function fetchGuardRate(): Promise<number> {
    try {
        const rateDoc = await getDoc(doc(db, 'settings', 'guard_rates'));
        if (rateDoc.exists()) {
            return rateDoc.data().currentRate || 0;
        }
        return 0;
    } catch (error) {
        console.error('Error fetching guard rate:', error);
        return 0;
    }
}

/**
 * Update the daily income rate for guards
 */
export async function updateGuardRate(rate: number, uid: string): Promise<void> {
    try {
        await setDoc(doc(db, 'settings', 'guard_rates'), {
            currentRate: rate,
            updatedAt: new Date(),
            updatedBy: uid
        });
    } catch (error) {
        console.error('Error updating guard rate:', error);
        throw error;
    }
}
